import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { creatorsAPI } from '../api/creators';

const CARD_BG = 'rgba(255,255,255,0.08)';
const CARD_BORDER = 'rgba(255,255,255,0.14)';
const COLORS = { twitch: '#9146ff', youtube: '#ff0000', kick: '#00e70a' };
// Kick's API exposes no follower count; its series (if ever snapshotted) is paid subscribers.
const LABELS = { twitch: 'Twitch followers', youtube: 'YouTube subscribers', kick: 'Kick subscribers' };

const fmt = (v) => {
  if (v == null) return '—';
  if (v >= 1e6) return `${(v / 1e6).toFixed(v >= 1e7 ? 0 : 1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}k`;
  return String(v);
};

const GrowthTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'rgba(20,20,30,0.95)', border: `1px solid ${CARD_BORDER}` }}>
      <div className="text-white mb-1">{label}</div>
      {payload.map((p) => (
        p.value != null && (
          <div key={p.dataKey} style={{ color: p.stroke }}>
            {LABELS[p.dataKey] || p.dataKey}: {Number(p.value).toLocaleString()}
          </div>
        )
      ))}
    </div>
  );
};

/**
 * Follower/subscriber growth-over-time chart for a creator.
 * Data comes from daily CreatorStatsSnapshot rows (collected nightly at 5:15am
 * UTC; history starts 2026-06-10). Until a couple of days of snapshots exist
 * the card shows a "tracking active" state instead of an empty chart.
 */
const CreatorGrowthChart = ({ creatorId }) => {
  const [growth, setGrowth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    creatorsAPI.getGrowth(creatorId, 90)
      .then((res) => { if (!cancelled) setGrowth(res.data); })
      .catch(() => { if (!cancelled) setGrowth(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [creatorId]);

  if (loading) return null;
  const platforms = growth?.platforms || {};
  const activeKeys = Object.keys(platforms).filter((p) => COLORS[p] && platforms[p].length > 0);
  if (activeKeys.length === 0) return null; // no snapshots at all yet — hide the card

  // Merge per-platform series into one row per day
  const byDay = new Map();
  for (const p of activeKeys) {
    for (const snap of platforms[p]) {
      if (!byDay.has(snap.day)) byDay.set(snap.day, { date: snap.day.slice(5) }); // MM-DD
      byDay.get(snap.day)[p] = snap.followers;
    }
  }
  const chartData = [...byDay.values()];
  const distinctDays = byDay.size;

  // Window deltas per platform (first vs last snapshot)
  const deltas = activeKeys.map((p) => {
    const series = platforms[p];
    const first = series[0], last = series[series.length - 1];
    return { platform: p, current: last.followers, change: last.followers - first.followers };
  });

  return (
    <div className="rounded-xl p-4" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <TrendingUp size={14} className="text-white" /> Audience growth
        </h3>
        <div className="flex items-center gap-3">
          {deltas.map((d) => (
            <span key={d.platform} className="text-[11px]" style={{ color: COLORS[d.platform] }}>
              {fmt(d.current)}
              {distinctDays > 1 && d.change !== 0 && (
                <span className={d.change > 0 ? 'text-emerald-400' : 'text-red-400'}>
                  {' '}{d.change > 0 ? '+' : ''}{fmt(Math.abs(d.change)) === '—' ? d.change : (d.change > 0 ? '' : '-') + fmt(Math.abs(d.change))}
                </span>
              )}
            </span>
          ))}
        </div>
      </div>

      {distinctDays < 2 ? (
        <p className="text-xs text-white">
          Daily growth tracking is active (since {platforms[activeKeys[0]][0].day}). The trend line appears
          once a few days of snapshots have accumulated.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <defs>
              {activeKeys.map((p) => (
                <linearGradient key={p} id={`growthGrad-${p}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS[p]} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={COLORS[p]} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 7) - 1)} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={fmt} domain={['auto', 'auto']} />
            <Tooltip content={<GrowthTooltip />} />
            {activeKeys.map((p) => (
              <Area key={p} type="monotone" dataKey={p} stroke={COLORS[p]} strokeWidth={2} fill={`url(#growthGrad-${p})`} dot={chartData.length < 15} connectNulls name={LABELS[p]} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default CreatorGrowthChart;
